import { useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  List,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconAlertCircle, IconDownload, IconSearch } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { GuestSearchRow } from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import {
  ADMIN_PRIVACY_KEY,
  eraseGuest,
  fetchErasures,
  fetchMailingLists,
  mailingListExportUrl,
  searchGuests,
} from '@/features/privacy/api';

/**
 * Персональные данные: рассылка, обращения, журнал удалений (docs/PRIVACY.md).
 *
 * Здесь Дарья делает то, что обещают её документы: отправляет рассылку только
 * согласившимся и с работающей ссылкой «Отписаться», исполняет просьбы
 * удалить данные — в том числе гостей без аккаунта — и видит, что и когда
 * удалено.
 */

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export function AdminPrivacyPage() {
  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Персональные данные"
        description="Список для рассылки, удаление данных по обращению и журнал удалений."
      />
      <Stack gap="lg">
        <Procedure />
        <MailingLists />
        <GuestErasure />
        <ErasureLog />
      </Stack>
    </>
  );
}

function Procedure() {
  return (
    <Paper withBorder p="lg" radius="lg">
      <Stack gap="xs">
        <Title order={4}>Если пришло обращение</Title>
        <Text size="sm" c="var(--text-muted)">
          По политике ответ — в течение 10 рабочих дней. Обращения приходят на support@creativitas.ru.
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            <b>«Отпишите от рассылок»</b> — найдите человека в{' '}
            <Anchor component={Link} to="/admin/users">
              «Пользователях»
            </Anchor>
            , «Доступ» → «Согласия» → «Отозвать по обращению». И уберите адрес из сервиса рассылок.
          </List.Item>
          <List.Item>
            <b>«Удалите мой аккаунт / отзываю согласие на обработку данных»</b> — там же, «Удалить
            аккаунт и данные». Человек может сделать это и сам в профиле.
          </List.Item>
          <List.Item>
            <b>Гость без аккаунта</b> («я играла у такого-то ведущего, удалите моё имя») — найдите его
            ниже по имени и обезличьте.
          </List.Item>
          <List.Item>
            Убедитесь, что письмо пришло с той же почты, что у аккаунта: удалять чужие данные по
            просьбе постороннего нельзя.
          </List.Item>
        </List>
      </Stack>
    </Paper>
  );
}

function MailingLists() {
  const query = useQuery({ queryKey: [...ADMIN_PRIVACY_KEY, 'mailing'], queryFn: fetchMailingLists });

  return (
    <Paper withBorder p="lg" radius="lg">
      <Stack gap="md">
        <div>
          <Title order={4}>Список для рассылки</Title>
          <Text size="sm" c="var(--text-muted)">
            Только те, чьё согласие действует сейчас и чья почта подтверждена. Скачивайте свежий
            список перед каждой рассылкой: кто-то мог отписаться.
          </Text>
        </div>
        {query.isLoading ? (
          <LoadingState />
        ) : query.isError || !query.data ? (
          <ErrorState onRetry={() => void query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <Text size="sm" c="var(--text-muted)">
            Необязательных согласий на экране регистрации нет — рассылать некому.
          </Text>
        ) : (
          query.data.items.map((list) => (
            <Group key={list.consentId} justify="space-between" align="flex-start" wrap="wrap" gap="sm">
              <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
                <Text size="sm">{list.text}</Text>
                <Text size="xs" c="var(--text-muted)">
                  Согласны: {list.subscribed}, из них с подтверждённой почтой: {list.verified}
                </Text>
              </Stack>
              <Button
                component="a"
                href={mailingListExportUrl(list.consentId)}
                download
                size="xs"
                variant="light"
                leftSection={<IconDownload size={14} />}
                disabled={list.verified === 0}
              >
                Скачать таблицу
              </Button>
            </Group>
          ))
        )}
        <Alert color="gold" variant="light" icon={<IconAlertCircle size={17} />}>
          В таблице у каждого адреса — личная ссылка «Отписаться». Вставьте её в письмо вместо общей
          ссылки сервиса рассылок (в сервисе это поле подстановки): тогда отписка запишется здесь, и
          человек не попадёт в следующий список.
        </Alert>
      </Stack>
    </Paper>
  );
}

function GuestErasure() {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);

  const results = useQuery({
    queryKey: [...ADMIN_PRIVACY_KEY, 'guests', submitted],
    queryFn: () => searchGuests(submitted),
    enabled: submitted.length >= 2,
  });

  const erase = useMutation({
    mutationFn: (participantId: string) => eraseGuest(participantId),
    onSuccess: () => {
      setConfirming(null);
      void queryClient.invalidateQueries({ queryKey: ADMIN_PRIVACY_KEY });
      notifications.show({ color: 'teal', message: 'Гость обезличен' });
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  return (
    <Paper withBorder p="lg" radius="lg">
      <Stack gap="md">
        <div>
          <Title order={4}>Гость без аккаунта</Title>
          <Text size="sm" c="var(--text-muted)">
            Гость входит в партию по ссылке и оставляет только имя. Его имя заменится на «Удалённый
            участник» — в списке участников и в журнале партии; партия ведущего останется.
          </Text>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(q.trim());
          }}
        >
          <Group gap="xs" align="flex-end">
            <TextInput
              label="Имя гостя"
              placeholder="Мария"
              value={q}
              onChange={(event) => setQ(event.currentTarget.value)}
              style={{ flex: 1, maxWidth: 360 }}
            />
            <Button type="submit" variant="light" leftSection={<IconSearch size={16} />} disabled={q.trim().length < 2}>
              Найти
            </Button>
          </Group>
        </form>

        {submitted.length < 2 ? null : results.isLoading ? (
          <LoadingState />
        ) : results.isError || !results.data ? (
          <ErrorState onRetry={() => void results.refetch()} />
        ) : results.data.items.length === 0 ? (
          <Text size="sm" c="var(--text-muted)">
            Гостей с таким именем нет.
          </Text>
        ) : (
          <ScrollArea>
            <Table miw={720} verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Гость</Table.Th>
                  <Table.Th>Партия</Table.Th>
                  <Table.Th>Ведущий</Table.Th>
                  <Table.Th>Вошёл</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {results.data.items.map((guest) => (
                  <GuestRow
                    key={guest.participantId}
                    guest={guest}
                    confirming={confirming === guest.participantId}
                    busy={erase.isPending}
                    onAsk={() => setConfirming(guest.participantId)}
                    onCancel={() => setConfirming(null)}
                    onConfirm={() => erase.mutate(guest.participantId)}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Stack>
    </Paper>
  );
}

function GuestRow({
  guest,
  confirming,
  busy,
  onAsk,
  onCancel,
  onConfirm,
}: {
  guest: GuestSearchRow;
  confirming: boolean;
  busy: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" fw={600}>
          {guest.displayName}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Badge size="xs" color={guest.sessionKind === 'game' ? 'gold' : 'teal'}>
            {guest.sessionKind === 'game' ? 'Игра' : 'МАК-сессия'}
          </Badge>
          <Text size="sm">{guest.sessionTitle}</Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{guest.hostDisplayName}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="var(--text-muted)">
          {formatDate(guest.joinedAt)}
        </Text>
      </Table.Td>
      <Table.Td>
        {confirming ? (
          <Group gap={4} wrap="nowrap">
            <Button size="xs" color="red" loading={busy} onClick={onConfirm}>
              Обезличить
            </Button>
            <Button size="xs" variant="default" onClick={onCancel}>
              Отмена
            </Button>
          </Group>
        ) : (
          <Button size="xs" variant="subtle" color="red" onClick={onAsk}>
            Удалить имя…
          </Button>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

function ErasureLog() {
  const query = useQuery({ queryKey: [...ADMIN_PRIVACY_KEY, 'erasures'], queryFn: fetchErasures });

  return (
    <Paper withBorder p="lg" radius="lg">
      <Stack gap="md">
        <div>
          <Title order={4}>Журнал удалений</Title>
          <Text size="sm" c="var(--text-muted)">
            Имён и почт здесь нет — только когда, что и кем. По служебному номеру сервер повторяет
            удаление, если базу восстановят из копии, сделанной раньше.
          </Text>
        </div>
        {query.isLoading ? (
          <LoadingState />
        ) : query.isError || !query.data ? (
          <ErrorState onRetry={() => void query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <Text size="sm" c="var(--text-muted)">
            Удалений пока не было.
          </Text>
        ) : (
          <ScrollArea>
            <Table miw={640} verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Когда</Table.Th>
                  <Table.Th>Что</Table.Th>
                  <Table.Th>Кто запустил</Table.Th>
                  <Table.Th>Служебный номер</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {query.data.items.map((item) => (
                  <Table.Tr key={`${item.kind}-${item.subjectId}-${item.at}`}>
                    <Table.Td>
                      <Text size="sm">{formatDate(item.at)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{item.kind === 'user' ? 'Аккаунт' : 'Гость в партии'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {item.via === 'self'
                          ? 'сам человек в профиле'
                          : `администратор${item.actorDisplayName ? ` ${item.actorDisplayName}` : ' (с сервера)'}`}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="var(--text-muted)" ff="monospace">
                        {item.subjectId}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Stack>
    </Paper>
  );
}
